import { engagementTypeShort } from '../../utils/engagementLabel';
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { Column } from '../DataTable';
import StatusBadge from '../StatusBadge';
import Badge from '../ui/Badge';
import { priorityTone } from '../ui/tone';
import MilestoneProgressBar from '../projects/MilestoneProgressBar';
import { actualHoursTitle, hoursText } from '../projects/projectModel';
import { formatDate } from '../../utils/format';
import { getPriorityLabel } from '../../types';
import { assigneeNames, assigneesSummary, overdueDays, taskMilestoneName, taskProgress } from './taskRowModel';
import { OrgName } from '../OrgLink';
import { useInternalEngagementId } from '../../hooks/useInternalEngagementId';
import { useInternalOrgId } from '../../hooks/useInternalOrg';
import { taskListContainerText, taskTypeText } from '../../utils/directUnderOte';

/** What a task row of the D-62 columns reads (the `GET /tickets` row). */
export interface TaskListRow {
  id: string;
  title: string;
  status: string;
  priority: string;
  organization_name?: string | null;
  /** T-56 (D-188): the company's row reads "OTE, עבודה פנימית". */
  organization_id?: string | null;
  engagement_type?: string | null;
  engagement_name?: string | null;
  project_name?: string | null;
  /** D-274b: a task "ישירות תחת OTE" sits on the hidden "עבודה פנימית" — the cell reads that label. */
  engagement_id?: string | null;
  direct_under_ote?: boolean | null;
  milestones?: { id: string; name: string }[] | null;
  agents?: { id: string; name: string }[] | null;
  agent_name?: string | null;
  assigned_agent_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  estimated_hours?: number | string | null;
  actual_hours?: number | string | null;
  non_billable_hours?: number | string | null;
  cancelled_at?: string | null;
  /** FU-19 — the task of an "אפיון" card (P-07). */
  characterization_id?: string | null;
  /** P-10e (D-82) — the saved bar (tickets.progress_percent). */
  progress_percent?: number | null;
}

/** D-274b — the "project / engagement" cell: "ישירות תחת OTE" for the hidden internal container. */
function ContainerCell({ task, t }: { task: TaskListRow; t: TFunction }) {
  const internalEngagementId = useInternalEngagementId();
  return small(taskListContainerText(task, internalEngagementId, t('engagementChain.directlyUnderOte')), 'max-w-36');
}

/** D-274b — the "type" cell: a task of the company (directly under OTE or in an internal project) reads "עבודה פנימית", not "פיתוח". */
function TypeCell({ task, t }: { task: TaskListRow; t: TFunction }) {
  const internalOrgId = useInternalOrgId();
  const internalEngagementId = useInternalEngagementId();
  const text = taskTypeText(task, { internalOrgId, internalEngagementId }, t('tasks.list.typeInternalWork'), (ty) => engagementTypeShort(ty, t));
  return small(text, 'max-w-24');
}

// C-31 (clarity, N-05): an empty cell is dimmed, so the eye skips it.
const dash = <span className="text-text-disabled" aria-hidden="true">—</span>;
const small = (text: string | null | undefined, w = 'max-w-36'): ReactNode =>
  (text ? <span className={`block ${w} truncate text-xs text-text-2`} title={text}>{text}</span> : dash);

/** "יעד" with the D-62 hint "באיחור X ימים" in colour (never a status). */
export function DueWithLate({ task, t }: { task: TaskListRow; t: TFunction }) {
  if (!task.due_date) return dash;
  const late = overdueDays(task.due_date, task);
  return (
    <span className="whitespace-nowrap text-xs tabular-nums text-text-2">
      {formatDate(task.due_date)}
      {late !== null && <span className="ms-1 font-medium text-danger-700" data-testid="task-row-late">{t('projectPage.lateBy', { count: late })}</span>}
    </span>
  );
}

/**
 * P-06/E (D-62) — the columns of "כל המשימות", in order (the ▶ column comes
 * from `useTaskRowActions().startColumn()`, the ⋯ from its `rowActions`):
 * שם / לקוח / סוג / פרויקט או התקשרות / אבן דרך / משויכים / סטטוס /
 * התקדמות / עדיפות / התחלה / יעד / מתוכנן / בפועל.
 * `hide` leaves out columns a tab does not need (e.g. the customer inside a customer).
 * C-25 (clarity, D-15): eight columns show at 1440 (משימה, לקוח, פרויקט, משויכים,
 * סטטוס, התקדמות, עדיפות, יעד); סוג, אבן דרך, התחלה, מתוכנן and בפועל are
 * priority 4 (from 2xl). `assignCell` (C-31) draws the empty assignees cell.
 */
export function taskListColumns<T extends TaskListRow>(t: TFunction, opts: { hide?: string[]; sortable?: string[]; statusCell?: (task: T) => ReactNode; assignCell?: (task: T) => ReactNode; onFillEstimate?: (task: T) => void } = {}): Column<T>[] {
  const hide = new Set(opts.hide ?? []);
  const sortable = new Set(opts.sortable ?? []);
  const cols: Column<T>[] = [
    {
      key: 'title',
      header: t('tasks.list.colTask'),
      link: true,
      sortable: sortable.has('title'),
      render: (task) => <bdi className={task.cancelled_at ? 'line-through decoration-text-disabled' : ''}>{task.title}</bdi>,
      trail: (task) => (task.cancelled_at
        ? <Badge>{t('tickets.cancelledLabel')}</Badge>
        // FU-19 — an "אפיון" task carries the label in every task table.
        : task.characterization_id ? <Badge tone="info">{t('characterization.add')}</Badge>
          // D-328 — a subtle "בלי הערכה" for the team lead / project manager (opts.onFillEstimate is passed only to them); a click opens the edit.
          : (opts.onFillEstimate && task.status !== 'completed' && !(Number(task.estimated_hours) > 0)
            ? <button type="button" className="text-xs text-text-disabled underline-offset-2 hover:underline" data-testid="task-no-estimate" onClick={(e) => { e.stopPropagation(); opts.onFillEstimate!(task); }}>{t('tasks.list.noEstimateMark')}</button>
            : null)),
    },
    { key: 'customer', header: t('tasks.list.colCustomer'), priority: 3, width: 'w-28', render: (task) => (task.organization_name
      ? <span className="block max-w-28 truncate text-xs text-text-2"><OrgName orgId={task.organization_id} name={task.organization_name} /></span>
      : dash) },
    {
      key: 'type',
      header: t('tasks.list.colType'),
      priority: 4,
      width: 'w-24',
      render: (task) => <TypeCell task={task} t={t} />,
    },
    {
      key: 'container',
      header: t('tasks.list.colContainer'),
      priority: 2,
      width: 'w-36',
      render: (task) => <ContainerCell task={task} t={t} />,
    },
    { key: 'milestone', header: t('tasks.list.colMilestone'), priority: 4, width: 'w-32', render: (task) => small(taskMilestoneName(task), 'max-w-32') },
    {
      key: 'assignees',
      header: t('tasks.list.colAssignees'),
      priority: 2,
      width: 'w-32',
      // C-31 (clarity, N-05, N-06): two names "+N" with the full list in `title`; empty = "+ שיוך" (staff) or a dimmed "לא משויך".
      render: (task) => {
        const names = assigneeNames(task as any);
        if (names.length === 0) return opts.assignCell?.(task) ?? <span className="text-xs text-text-disabled">{t('tasks.list.unassigned')}</span>;
        const { shown, more } = assigneesSummary(names);
        return (
          <span className="block max-w-32 truncate text-xs text-text-2" title={names.join(', ')}>
            {shown}{more > 0 && <span className="ms-1 tabular-nums text-text-3">{t('tasks.list.moreAssignees', { n: more })}</span>}
          </span>
        );
      },
    },
    { key: 'status', header: t('common.status'), width: 'w-28', render: (task) => (opts.statusCell ? opts.statusCell(task) : <StatusBadge status={task.status as any} />) },
    {
      key: 'progress',
      header: t('tasks.list.colProgress'),
      priority: 2,
      width: 'w-28',
      card: 'none',
      render: (task) => <MilestoneProgressBar value={taskProgress(task.status, task.progress_percent)} label={task.title} className="w-24" />,
    },
    {
      key: 'priority',
      header: t('common.priority'),
      priority: 3,
      width: 'w-24',
      // C-32 (clarity, D-22): a priority tag is outlined, not filled, so it never reads as a status.
      render: (task) => <Badge variant="outline" tone={priorityTone(task.priority as any)}>{getPriorityLabel(task.priority as any, t as any)}</Badge>,
    },
    {
      key: 'start_date',
      header: t('tickets.startDate'),
      align: 'end',
      priority: 4,
      width: 'w-24',
      sortable: sortable.has('start_date'),
      render: (task) => (task.start_date ? <span className="whitespace-nowrap text-xs tabular-nums text-text-2">{formatDate(task.start_date)}</span> : dash),
    },
    {
      key: 'due_date',
      header: t('tickets.dueDate'),
      align: 'end',
      width: 'w-32',
      sortable: sortable.has('due_date'),
      render: (task) => <DueWithLate task={task} t={t} />,
    },
    {
      key: 'planned',
      header: t('tasks.list.colPlanned'),
      align: 'end',
      priority: 4,
      width: 'w-20',
      render: (task) => (task.estimated_hours != null && Number(task.estimated_hours) > 0
        ? <span className="text-xs tabular-nums text-text-2">{hoursText(Number(task.estimated_hours))}</span>
        : dash),
    },
    {
      key: 'actual',
      header: t('tasks.list.colActual'),
      align: 'end',
      priority: 4,
      width: 'w-20',
      render: (task) => <span className="text-xs tabular-nums text-text-2" title={actualHoursTitle(task.actual_hours, task.non_billable_hours, t)}>{hoursText(Number(task.actual_hours ?? 0))}</span>,
    },
  ];
  return cols.filter((c) => !hide.has(c.key));
}
